import { computed, ref, inject, provide } from '@vue/composition-api'
import moment from 'moment'
import uniq from 'lodash/uniq'
import { ALLOWED_ACTION_CONDITIONS, ALL_ACTION_CONDITIONS } from '@/components/DynamicForms/fieldUtils'
const { isEmpty } = require('@shared/utils/isEmpty')
const { TYPE_OPTIONS_BY_QUESTION_TYPE } = require('@shared/constants/conditionTypesOptions')

export const CURRENT_FIELD_VALUE_INDICATOR = '$this'

const conditionTypesSymbol = Symbol('conditionTypesSymbol')

const isCheck = (expectedValue, sourceValue) => {
  const normalizedSourceValue = Array.isArray(sourceValue)
    ? sourceValue.join('')
    : String(sourceValue)
  const normalizedExpectedValue = Array.isArray(expectedValue)
    ? expectedValue.join('')
    : String(expectedValue)
  return normalizedSourceValue.toLowerCase() === normalizedExpectedValue.toLowerCase()
}

const containsCheck = (expectedValue, sourceValue) => {
  const normalizedSourceValue = Array.isArray(sourceValue)
    ? sourceValue.join(', ')
    : sourceValue

  return (String(normalizedSourceValue) || '').toLowerCase().includes(String(expectedValue).toLowerCase())
}

const startsWithCheck = (expectedValue, sourceValue) => {
  const normalizedSourceValue = Array.isArray(sourceValue)
    ? sourceValue.join(', ')
    : String(sourceValue)

  return (normalizedSourceValue || '').toLowerCase().startsWith(String(expectedValue).toLowerCase())
}

const emptyCheck = (_expectedValue, sourceValue) => isEmpty(sourceValue)

const isAfterCheck = (expectedValue, sourceValue) => {
  if (!sourceValue || !expectedValue) return false
  return moment.utc(sourceValue).isAfter(moment.utc(expectedValue))
}

const isBeforeCheck = (expectedValue, sourceValue) => {
  if (!sourceValue || !expectedValue) return false
  return moment.utc(sourceValue).isBefore(moment.utc(expectedValue))
}

const isSameDateCheck = (expectedValue, sourceValue) => {
  if (!sourceValue || !expectedValue) return false
  return moment.utc(sourceValue).isSame(moment.utc(expectedValue), 'day')
}

const isGreaterThanCheck = (expectedValue, sourceValue) => {
  if (!sourceValue || !expectedValue) return false
  return sourceValue > expectedValue
}

const isLowerThanCheck = (expectedValue, sourceValue) => {
  if (!sourceValue || !expectedValue) return false
  return sourceValue < expectedValue
}

const isDuplicateCheck = (_, sourceValue, thisValue) => {
  if (!sourceValue?.length) return false
  const totalMatches = Array.isArray(sourceValue)
    ? sourceValue.filter(v => v === thisValue)
    : []

  if (totalMatches.length >= 2) return true
  else return false
}

const hasDuplicatesCheck = (_expectedValue, sourceValue) => {
  if (!sourceValue) return false
  const totalLength = sourceValue.length
  const uniqueLength = uniq(sourceValue).length
  return totalLength !== uniqueLength
}

const BASE_CONDITION_TYPES = {
  is: isCheck,
  isNot: (expectedValue, sourceValue) => !isCheck(expectedValue, sourceValue),
  contains: containsCheck,
  doesNotContain: (expectedValue, sourceValue) => !containsCheck(expectedValue, sourceValue),
  startsWith: startsWithCheck,
  isEmpty: emptyCheck,
  isNotEmpty: (expectedValue, sourceValue) => !emptyCheck(expectedValue, sourceValue),
  isAfter: isAfterCheck,
  isBefore: isBeforeCheck,
  isSameDate: isSameDateCheck,
  isGreaterThan: isGreaterThanCheck,
  isLowerThan: isLowerThanCheck,
  isDuplicate: isDuplicateCheck,
  hasDuplicates: hasDuplicatesCheck,
}

const VALUELESS_TYPES = [ 'isEmpty', 'isNotEmpty', 'isDuplicate', 'hasDuplicates' ]

const BASE_TYPE_OPTIONS = [
  'is',
  'isNot',
  'contains',
  'startsWith',
  'doesNotContain',
  'isEmpty',
  'isNotEmpty',
]
function availableTypeOptions (fieldType = '') {
  const dotIndex = fieldType.indexOf('.')
  const typeEntry = dotIndex > -1
    ? TYPE_OPTIONS_BY_QUESTION_TYPE.root[fieldType.slice(0, dotIndex)]
    : TYPE_OPTIONS_BY_QUESTION_TYPE.direct[fieldType]
  return BASE_TYPE_OPTIONS.filter(
    optionType => !(typeEntry?.exclude || []).includes(optionType)
  ).concat(typeEntry?.include || [])
}

export function useAllowedConditions ({ question }) {
  const allowedActionConditionsGetterFn = inject(ALLOWED_ACTION_CONDITIONS, () => ALL_ACTION_CONDITIONS)
  const allowedActionConditions = computed(() => allowedActionConditionsGetterFn(question.value))
  return {
    allowedActionConditions,
  }
}

export default function useConditionTypes () {
  const conditionTypesArray = inject(conditionTypesSymbol, ref([ BASE_CONDITION_TYPES ]))
  provide(conditionTypesSymbol, conditionTypesArray)

  const conditionTypes = computed(() => conditionTypesArray.value
    .reduce((types, type) => ({ ...types, ...type }), {})
  )
  /**
   * @example
   * addTypes({
   *  someNewType: () => {}
   * })
   */
  function addTypes (typeOrTypes) {
    const types = Array.isArray(typeOrTypes) ? typeOrTypes : [ typeOrTypes ]
    conditionTypesArray.value.push(...types)
  }

  /**
   * Returns a method to compare item with source value of a given index.
   * Most of the time compares item value with source value.
   * fieldValue is taken into consideration only when item.value === '$this'
   */
  function evaluateCondition (sourceValues, fieldValue, formValue) {
    return (item, index) => isPassingCondition({
      type: item.type,
      expected: item.value,
      sourceValue: sourceValues[index],
      thisValue: fieldValue,
      formValue: formValue,
    })
  }

  function isPassingCondition ({ type, expected, sourceValue, thisValue, formValue }) {
    let expectedValue
    if (expected === CURRENT_FIELD_VALUE_INDICATOR) {
      expectedValue = thisValue
    } else if (expected && String(expected).startsWith('$')) {
      const selectedFormField = expected.slice(1)
      expectedValue = formValue[selectedFormField]
    } else {
      expectedValue = expected
    }

    const check = conditionTypes.value[type]
    if (check) return check(expectedValue, sourceValue, thisValue)
    throw Error(`Condition "${type}" is not supported`)
  }

  function isValuelessType (type) {
    return VALUELESS_TYPES.includes(type)
  }

  return {
    conditionTypes,
    addTypes,
    evaluateCondition,
    isValuelessType,
    availableTypeOptions,
  }
}
